import { useMemo, useRef, useState } from 'react'
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tab,
  TabList,
  Tabs,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { useAccessCheck } from '@/hooks/useAccess'
import { useThemeColors } from '@/hooks/useThemeColors'
import { apiClient } from '@/lib/api/client'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import type { GeographicEntityConfig } from '@/config/geographicEntities'

type GeographicEntityListPageProps = {
  config: GeographicEntityConfig
}

export function GeographicEntityListPage({ config }: GeographicEntityListPageProps) {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const access = useAccessCheck()

  const [activeTab, setActiveTab] = useState<'all' | 'trash'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const dataSource = activeTab === 'trash' ? config.archivedPath : config.apiPath

  const statusFilterOptions = useMemo(
    () => [
      { value: 'Active', label: 'Active' },
      { value: 'Inactive', label: 'Inactive' },
    ],
    [],
  )

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        ...config.columns,
        active: {
          name: 'Status',
          withSort: true,
          withFilter: true,
          filterOptions: statusFilterOptions,
          badge: { Active: 'green', Inactive: 'yellow' },
          badgeLabels: { Active: 'Active', Inactive: 'Inactive' },
        },
      },
      dataSource,
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: `No ${config.title.toLowerCase()} found`,
    }),
    [config, dataSource, statusFilterOptions],
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access],
  )

  const handleConfirmBulkAction = async () => {
    const ids = tableRef.current?.getSelectedRows() || []
    if (ids.length === 0) return
    setIsBulkProcessing(true)
    try {
      let url: string
      let msg: string
      if (bulkAction === 'restore') {
        url = `${config.apiPath}/bulk/restore`
        msg = `${config.title} restored successfully`
      } else {
        url =
          activeTab === 'trash'
            ? `${config.apiPath}/bulk/force-delete`
            : `${config.apiPath}/bulk/delete`
        msg =
          activeTab === 'trash'
            ? `${config.title} permanently deleted`
            : `${config.title} moved to trash`
      }
      await apiClient.post(url, { ids })
      showSuccess('Success', msg)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (e) {
      showError(e)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flex="1" minH={0} overflow="hidden" display="flex" flexDirection="column">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md">{config.title}</Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage {config.title.toLowerCase()} master data
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <HStack justify="space-between" px={4} width="100%">
                <Tabs index={activeTab === 'all' ? 0 : 1} onChange={(i) => setActiveTab(i === 0 ? 'all' : 'trash')}>
                  <TabList>
                    <Tab>
                      All <Badge ml={2}>{totalRows}</Badge>
                    </Tab>
                    {totalTrash > 0 && (
                      <Tab>
                        Trash <Badge ml={2}>{totalTrash}</Badge>
                      </Tab>
                    )}
                  </TabList>
                </Tabs>
                {access.can_create && (
                  <Button colorScheme="blue" onClick={() => navigate(`${config.basePath}/create`)}>
                    Create {config.singular}
                  </Button>
                )}
              </HStack>
            </CardHeader>

            <CardHeader py={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack>
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e) => setBulkAction(e.target.value)}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!bulkAction) return showWarning('Select an action')
                        if ((tableRef.current?.getSelectedRows() || []).length === 0) {
                          return showWarning('Select at least one item')
                        }
                        setModalMessage('Apply this bulk action?')
                        onOpen()
                      }}
                    >
                      Apply
                    </Button>
                  </HStack>
                )}
                <InputGroup size="sm" maxW="320px">
                  <Input
                    ref={searchRef}
                    placeholder="Search..."
                    defaultValue={searchQuery}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const q = searchRef.current?.value || ''
                        setSearchQuery(q)
                        tableRef.current?.applyFilter('search', q)
                      }
                    }}
                  />
                  <InputRightElement>
                    <IconButton
                      aria-label="Search"
                      size="sm"
                      variant="ghost"
                      icon={<Search size={16} />}
                      onClick={() => {
                        const q = searchRef.current?.value || ''
                        setSearchQuery(q)
                        tableRef.current?.applyFilter('search', q)
                      }}
                    />
                  </InputRightElement>
                </InputGroup>
              </HStack>
            </CardHeader>

            <CardBody p={0} mx={5} flex="1" minH={0}>
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={{ search: searchQuery }}
                access={permissions}
                setSubSub={(all, trashed) => {
                  setTotalRows(all)
                  setTotalTrash(trashed)
                }}
                onOpenModal={(id) => navigate(`${config.basePath}/${id}`)}
                filterOptions={{ active: statusFilterOptions }}
              />
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" isLoading={isBulkProcessing} onClick={handleConfirmBulkAction}>
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
